import { LogUserActivity } from '@/apis/users/requests'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { Button } from '@/common/components/Button.tsx'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import { FamilyApplicationData } from '@/layouts/FamilyApplicationLayout.tsx'
import { selectMode } from '@/storage/applications/applicationStatusSlice'
import {
  RefreshFamilyApplication,
  selectFamilyApplication,
} from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { AppDispatch } from '@/storage/store'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { LOG_TYPES } from '../constants'
import useFamilyApplicationRouting from '../routing/useFamilyApplicationRouting'
import { getStudentDetailsStartRoute } from '../utils'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { ClockIcon } from '@/app/common/icons/general'

const ResidencySuccessVerified = () => {
  const { t: translate } = useTranslation()
  const navigateTo = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const applicantDetails = useOutletContext<FamilyApplicationData>()
  const residencyManualData = useSelector(selectFamilyApplication)
  const { onModuleEnd } = useFamilyApplicationRouting(residencyManualData)
  const editMode = useSelector(selectMode)
  const programId = useSelector(selectProgramID)

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Identify and Residency Application being reviewed warning page',
      }) // Log page view
      hasLoggedPageView.current = true // Mark as executed
    }
  }, [])

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1 className="text-3xl font-medium text-gray-900">
          {translate('identity_residency_verify_txt')}
        </h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="items-center justify-center">
        <ClockIcon className="text-7xl text-primary mb-7" />
        <h3 className="text-xl font-medium text-center text-gray-800 mb-8">
          {translate('residency_verify_complete')}
        </h3>

        <Card className="w-full flex-shrink-0">
          <CardHeader className="font-medium text-md">
            {translate('information')}
          </CardHeader>
          <CardContent className="w-full">
            <ul>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">{translate('name')}</span>
                <span className="text-neutral-900 font-medium">
                  {residencyManualData?.first_name}{' '}
                  {residencyManualData?.last_name}
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">{translate('address')}</span>
                <span className="text-neutral-900 font-medium">
                  {residencyManualData?.address}
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">
                  {translate('address')} 2
                </span>
                <span className="text-neutral-900 font-medium">
                  {residencyManualData?.address2}
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">{translate('city')}</span>
                <span className="text-neutral-900 font-medium">
                  {residencyManualData?.city}
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">{translate('state')}</span>
                <span className="text-neutral-900 font-medium">
                  {
                    applicantDetails.states.data.find((state) => {
                      return state.id == residencyManualData?.global_state_id
                    })?.name
                  }
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">{translate('zip')}</span>
                <span className="text-neutral-900 font-medium">
                  {residencyManualData?.zip_code}
                </span>
              </li>
              {residencyManualData?.county && (
                <li className="flex items-center justify-between py-2">
                  <span className="text-neutral-700">
                    {translate('county')}
                  </span>
                  <span className="text-neutral-900 font-medium">
                    {residencyManualData?.county}
                  </span>
                </li>
              )}
            <li className="flex items-center justify-between py-2">
                  <span className="text-neutral-700">
                    {translate('military_exempt')}
                  </span>
                  <span className="text-neutral-900 font-medium">
                    {residencyManualData?.is_military_exemption ? "Yes" : "No"}
                  </span>
                </li>
            </ul>
          </CardContent>
        </Card>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar className="flex-col">
        {' '}
        {editMode === 'none' && (
          <span className="text-gray-600 mb-2 text-center">
            {translate('up_next_student_details')}
          </span>
        )}
        <Button
          onClick={() => {
              navigateTo(onModuleEnd(AppRoutePaths.family_application.residency_verification.enter_details))
          }}
          className="h-9 mb-5"
        >
          {translate('edit_residency_details')}
        </Button>
        <Button
          onClick={() => {
            dispatch(RefreshFamilyApplication(programId))
            const nextRoute = getStudentDetailsStartRoute(
              applicantDetails.familyApplication
            )

            navigateTo(onModuleEnd(nextRoute))
          }}
          className="h-9 mb-5"
        >
          {translate('continue')}
        </Button>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
export default ResidencySuccessVerified
